import type { IProductVM } from '@/react/view-models/ProductVM';
import { IPrice, PriceModel } from '@/services/models/Price';
import type { IRatings } from '@/services/models/ReviewsModel';
import { Asyncable, Nullable } from '@/type-utils';
import { useConfig } from '@/react/hooks/useConfig';
import ProductDetailsVariant from '../ProductDetailsVariant';

/** Interface for Variation Settings hook. */
export interface IVariationSettingsHook {
  /** Flag for whether wishlist is enabled. */
  isWishlistEnabled: boolean;

  /** Flag for whether star ratings are shown. */
  isShowStarRatings: boolean;

  /** The name of the product. */
  name: string;

  /** The sku of the product. */
  sku: string;

  /** The ratings of the product. */
  ratings: Asyncable<Nullable<IRatings>>;

  /** The price model of the product. */
  priceModel: Nullable<IPrice>;

  /** The group name of the product. */
  groupName: Nullable<string>;
}

/**
 * Hook to get variation settings.
 * @param variant - The product variant to display.
 * @param product - The product to display.
 * @returns Wishlist enabled state, product model.
 */
export const useVariationSettings = (
  variant: ProductDetailsVariant,
  product: IProductVM
): IVariationSettingsHook => {
  const productConfig = useConfig('product');
  const isWishlistEnabled = productConfig.getSetting('wishlist.enable').value;

  const reviewsConfig = useConfig('reviews');
  const isShowStarRatings = reviewsConfig.getSetting(
    'customerReviews.show'
  ).value;

  const { price, name, sku, ratings, groupDisplayName } = product;
  const priceModel = price ? PriceModel.from(price) : null;

  return {
    isWishlistEnabled,
    isShowStarRatings,
    name,
    sku,
    ratings,
    priceModel,
    groupName: groupDisplayName
  };
};
